import {
    ChangeEventHandler,
    ClipboardEventHandler, FocusEventHandler, forwardRef,
    KeyboardEventHandler, MouseEvent,
    MouseEventHandler, ReactNode, Ref, useEffect, useImperativeHandle, useRef, useState
} from 'react';
import { IFormControl } from '../interfaces';
import EyeIcon from '../../svg/common/eye';

interface IFormTextField {
    id: string,
    label: string,
    control: IFormControl,
    maxLength?: number,
    focus?: boolean,
    disabled?:boolean,
    type?: string, // text or password

    // event handlers
    onBlur?: FocusEventHandler,
    onChange?: ChangeEventHandler,
    onClick?: MouseEventHandler,
    onKeyUp?: KeyboardEventHandler,
    onKeyDown?: KeyboardEventHandler,
    onPaste?: ClipboardEventHandler,

    // html content
    comment?: ReactNode,

    // styles
    rootClasses?: string,
    textBoxClasses?: string,

    // QA data attribute
    qa?: string,
    qaError?: string,
}

/**
 * Text field.
 */
const FormTextField = (props: IFormTextField, ref: Ref<HTMLInputElement>) => {

    // can be used for focus on page load
    const controlRef = useRef<HTMLInputElement>(null);
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);

    // Expose the internal input to the parent ref
    useImperativeHandle(ref, () => controlRef.current as HTMLInputElement, []);

    useEffect(() => {

        if(!props.focus) return;

        // on page load -> focus on the field
        if(!controlRef || !controlRef.current) return;
        controlRef.current.focus();
    }, [props.focus]);

    /**
     * handle password eye click
     */
    const handlePasswordToggle = (evt: MouseEvent<HTMLButtonElement>) => {
        setIsPasswordVisible(val => !val);
    };

    /**
     * define control type logic
     */
    const getControlType = (type: string|undefined) => {

        // default type is text
        if(!type) return 'text';

        // user clicked on the 'eye' inside the password field
        if(type === 'password' && isPasswordVisible) return 'text';

        return type;
    };

    return (
        <div className={ props.rootClasses ? props.rootClasses : 'mb-6'}>
            <div className={`w-form-field ${!props.control.isValid ? 'w-form-error' : ''} `}>

                <input
                    id={ props.id }
                    className={`w-form-tb ${ props.textBoxClasses || '' } ${ (props.control.value !== null && props.control.value !== undefined && props.control.value.length > 0) ? 'not-empty': '' } ${ props.type === 'password' ? 'pr-8' : '' }`}
                    type={ getControlType(props.type) }
                    maxLength={ props.maxLength }
                    value={ props.control.value }
                    onBlur={ props.onBlur }
                    onChange={ props.onChange }
                    onClick={  props.onClick }
                    onKeyUp={ props.onKeyUp }
                    onKeyDown={ props.onKeyDown }
                    onPaste={ props.onPaste }
                    ref={ controlRef }
                    disabled={ props.disabled }
                    data-qa={ props.qa }
                />

                <label htmlFor={ props.id } className="w-form-label">{ props.label }</label>

                {
                    props.type === 'password' && (
                        <button
                            type="button"
                            onClick={ handlePasswordToggle }
                            className="cursor-pointer absolute right-0 bg-white px-2 mr-px">
                            <EyeIcon
                                width="22px"
                                color={ isPasswordVisible ? '#9a9a9a' : '#bab8b8'}
                                title={ isPasswordVisible ? 'Hide Password' : 'Show Password' }
                            />
                        </button>
                    )
                }
            </div>

            { props.control.isValid && props.comment }

            {
                !props.control.isValid &&
                <div
                    className="text-red text-sm"
                    data-qa={ props.qaError }
                    aria-label={ `${ props.label } error: ${  props.control.error }` }
                    role="alert">{ props.control.error }</div>
            }
        </div>
    )
};

export default forwardRef<HTMLInputElement, IFormTextField>(FormTextField);